import {
    faArrowUpRight,
    faEye,
    faPlus,
    faShareAll,
    faXmark,
} from "@fortawesome/pro-light-svg-icons";
import { faDownload } from "@fortawesome/pro-regular-svg-icons";
import type { GridRowParams } from "@mui/x-data-grid";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { TrashAlt, VaporIcon } from "@vapor/react-icons";
import { useTheme } from "@vapor/react-material";
import type { Dispatch, ReactElement, SetStateAction } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDownloadTaxSubmissionFiles } from "../../../../../core/api/use-download-tax-submission-files/useDownloadTaxSubmissionFiles";
import { useDownloadTaxSubmissionOriginalFile } from "../../../../../core/api/use-download-tax-submission-original-file/useDownloadTaxSubmissionOriginalFile";
import { useDownloadTaxSubmissionSogei } from "../../../../../core/api/use-download-tax-submission-sogei/useDownloadTaxSubmissionSogei";
import { GovernmentAgencyType } from "../../../../../core/api/use-get-tax-submission-detail/TaxSubmissionDetailDTO";
import { TaxSubmissionStatus } from "../../../../../core/dtos/TaxSubmissionStatus";
import { ValidationDocument } from "../../../../../core/dtos/ValidationDocumentDto";
import { downloadFile } from "../../../../../core/utils/downloadFile";
import { createNavigationUrl, processUrn } from "../../../../utils/resolveUrn";
import type { RowModel } from "./cellRenderers";

interface UseRowActionsMenuParams {
    onClickRevokeTaxSubmission: (taxSubmissionId: string) => unknown;
    onClickPreviewSogeiOutcome: (content: string, loading: boolean) => unknown;
    setDeleteTaxSubmissionId: Dispatch<SetStateAction<string | undefined>>;
    setRejectedTaxSubmissionId: Dispatch<SetStateAction<string | undefined>>;
    setUpdateAgencyDataTaxSubmissionId: Dispatch<
        SetStateAction<string | undefined>
    >;
}

export const useRowActionsMenu = ({
    onClickRevokeTaxSubmission,
    onClickPreviewSogeiOutcome,
    setDeleteTaxSubmissionId,
    setRejectedTaxSubmissionId,
    setUpdateAgencyDataTaxSubmissionId,
}: UseRowActionsMenuParams) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const downloadSogeiMutation = useDownloadTaxSubmissionSogei();
    const downloadFilesMutation = useDownloadTaxSubmissionFiles();
    const downloadOriginalMutation = useDownloadTaxSubmissionOriginalFile();

    const onClickDownloadSogei = useCallback(
        async (taxSubmissionId: string, type: ValidationDocument) => {
            try {
                const resourceData = await downloadSogeiMutation.mutateAsync({
                    taxSubmissionId,
                    type,
                });
                downloadFile(
                    resourceData.content,
                    "esito_sogei.wri",
                    resourceData.type,
                );
            } catch {
                // error notification already shown by handleHttpError
            }
        },
        [downloadSogeiMutation],
    );

    const onClickPreviewSogeiValidation = useCallback(
        async (taxSubmissionId: string, type: ValidationDocument) => {
            onClickPreviewSogeiOutcome("", true);
            try {
                const resourceData = await downloadSogeiMutation.mutateAsync({
                    taxSubmissionId,
                    type,
                });
                onClickPreviewSogeiOutcome(
                    new TextDecoder("latin1").decode(resourceData.content),
                    false,
                );
            } catch {
                // reset loading state; error notification shown by handleHttpError
                onClickPreviewSogeiOutcome("", false);
            }
        },
        [downloadSogeiMutation, onClickPreviewSogeiOutcome],
    );

    return useCallback(
        (params: GridRowParams<RowModel>): ReactElement[] => {
            const taxSubmission = params.row;
            if (!taxSubmission) return [];

            const actions: ReactElement[] = [];
            const itemSx = {
                color: theme.palette.primary.interactiveDefault,
            };

            if (taxSubmission.isRevocable) {
                actions.push(
                    <GridActionsCellItem
                        icon={<VaporIcon icon={faXmark} color="interactive" />}
                        label={t(
                            "views.home.components.tax_submissions_table.actions.revoke_tax_submissions",
                        )}
                        sx={itemSx}
                        onClick={() =>
                            onClickRevokeTaxSubmission(params.row.id)
                        }
                        showInMenu
                    />,
                );
            }

            if (taxSubmission.isTaxSubmissionDeletable) {
                actions.push(
                    <GridActionsCellItem
                        icon={<TrashAlt color="interactive" />}
                        label={t(
                            "views.home.components.tax_submissions_table.actions.delete_tax_submissions",
                        )}
                        sx={itemSx}
                        onClick={() => setDeleteTaxSubmissionId(params.row.id)}
                        showInMenu
                    />,
                );
            }

            if (taxSubmission.validationDocument) {
                if (
                    taxSubmission.governmentAgency.type !==
                    GovernmentAgencyType.sdi
                ) {
                    actions.push(
                        <GridActionsCellItem
                            icon={
                                <VaporIcon icon={faEye} color="interactive" />
                            }
                            label={t(
                                "views.home.components.tax_submissions_table.actions.preview_sogei_file",
                            )}
                            sx={itemSx}
                            onClick={() =>
                                onClickPreviewSogeiValidation(
                                    params.row.id,
                                    taxSubmission.validationDocument!,
                                )
                            }
                            showInMenu
                        />,
                    );
                }

                actions.push(
                    <GridActionsCellItem
                        icon={
                            <VaporIcon icon={faDownload} color="interactive" />
                        }
                        label={t(
                            "views.home.components.tax_submissions_table.actions.download_sogei_file",
                        )}
                        sx={itemSx}
                        onClick={() =>
                            onClickDownloadSogei(
                                params.row.id,
                                taxSubmission.validationDocument!,
                            )
                        }
                        showInMenu
                    />,
                );
            }

            if (taxSubmission.status === TaxSubmissionStatus.REJECTED) {
                actions.push(
                    <GridActionsCellItem
                        icon={
                            <VaporIcon icon={faShareAll} color="interactive" />
                        }
                        label={t(
                            "views.home.components.tax_submissions_table.actions.send_rejected_tax_submission",
                        )}
                        sx={itemSx}
                        onClick={() =>
                            setRejectedTaxSubmissionId(params.row.id)
                        }
                        showInMenu
                    />,
                );
            }

            if (taxSubmission.isAgencyDataEditable) {
                actions.push(
                    <GridActionsCellItem
                        icon={<VaporIcon icon={faPlus} color="interactive" />}
                        label={t(
                            "views.home.components.tax_submissions_table.actions.update_agency_data",
                        )}
                        onClick={() =>
                            setUpdateAgencyDataTaxSubmissionId(params.row.id)
                        }
                        sx={itemSx}
                        showInMenu
                    />,
                );
            }

            if (taxSubmission.ownerApp) {
                actions.push(
                    <GridActionsCellItem
                        icon={
                            <VaporIcon
                                icon={faArrowUpRight}
                                color="interactive"
                                iconWeight="solid"
                            />
                        }
                        label={t(
                            "views.home.components.tax_submissions_table.actions.go_to_procedure",
                        )}
                        sx={itemSx}
                        onClick={() => {
                            const ownerApp = params.row.ownerApp!;
                            navigate(
                                createNavigationUrl(
                                    ownerApp.taxSubmissionPath ??
                                        processUrn(ownerApp.urn),
                                ),
                            );
                        }}
                        showInMenu
                    />,
                );
            }

            actions.push(
                <GridActionsCellItem
                    icon={<VaporIcon icon={faDownload} color="interactive" />}
                    label={t(
                        "views.home.components.tax_submission_detail_toolbar.other_actions.download_original_file",
                    )}
                    sx={itemSx}
                    onClick={async () => {
                        try {
                            const resourceData =
                                await downloadOriginalMutation.mutateAsync(
                                    taxSubmission.id,
                                );
                            downloadFile(
                                resourceData.content,
                                params.row.name,
                                resourceData.type,
                            );
                        } catch {
                            // error toast already shown via meta.errorMessage in the mutation hook
                        }
                    }}
                    showInMenu
                />,
            );

            if (
                taxSubmission.status === TaxSubmissionStatus.SENT ||
                taxSubmission.status === TaxSubmissionStatus.SENT_WITH_WARNINGS
            ) {
                actions.push(
                    <GridActionsCellItem
                        icon={
                            <VaporIcon icon={faDownload} color="interactive" />
                        }
                        label={t(
                            "views.home.components.tax_submission_detail_toolbar.other_actions.download_files",
                        )}
                        sx={itemSx}
                        onClick={async () => {
                            try {
                                const resourceData =
                                    await downloadFilesMutation.mutateAsync(
                                        taxSubmission.id,
                                    );
                                downloadFile(
                                    resourceData.content,
                                    params.row.name,
                                    resourceData.type,
                                );
                            } catch {
                                // error toast already shown via meta.errorMessage in the mutation hook
                            }
                        }}
                        showInMenu
                    />,
                );
            }

            return actions;
        },
        [
            t,
            theme,
            navigate,
            downloadFilesMutation,
            downloadOriginalMutation,
            onClickDownloadSogei,
            onClickPreviewSogeiValidation,
            onClickRevokeTaxSubmission,
            setDeleteTaxSubmissionId,
            setRejectedTaxSubmissionId,
            setUpdateAgencyDataTaxSubmissionId,
        ],
    );
};
